import {
  CompanyBasicDto,
  ValidateProcurementSupplierDto,
} from "@green0meter/api-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UseFormSetError } from "react-hook-form";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { DOMAIN_PROCUREMENT } from "../domain";

import { procurementApi } from "@api/services";
import { ValidateProcurementSupplierFormValues } from "@components/form";

export const ACTION_VALIDATE_PROCUREMENT_SUPPLIER = "VALIDATE_PROCUREMENT_SUPPLIER";

/*****************
 * Public route *
 *****************/

type SetAuthenticatorSecretRequest = ValidateProcurementSupplierDto;

const validateProcurementSupplier = async (
  param: SetAuthenticatorSecretRequest,
): Promise<CompanyBasicDto> => {
  const response =
    await procurementApi.procurementControllerValidateProcurementSupplier(param);

  return response.data;
};

export const useValidateProcurementSupplier = (
  setError?: UseFormSetError<ValidateProcurementSupplierFormValues>,
): {
  validateSupplierAsync: (
    param: SetAuthenticatorSecretRequest,
  ) => Promise<CompanyBasicDto>;
  isSuccessValidateSupplier: boolean;
  isPendingValidateSupplier: boolean;
} => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { mutateAsync, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_PROCUREMENT, ACTION_VALIDATE_PROCUREMENT_SUPPLIER],
    mutationFn: validateProcurementSupplier,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DOMAIN_PROCUREMENT] });
    },
    onError: (error: AxiosError) => {
      if (setError) {
        const apiError = error as {
          response?: { data?: { code?: string; message?: string } };
        };

        const code = apiError?.response?.data?.code;
        const message = apiError?.response?.data?.message;

        if (code === "company_not_found") {
          setError("vat", {
            message: t(message as string),
            type: "server",
          });
        } else if (code === "company_not_invited") {
          setError("vat", {
            message: t(message as string),
            type: "server",
          });
        }
      }
    },
  });

  return {
    validateSupplierAsync: mutateAsync,
    isSuccessValidateSupplier: isSuccess,
    isPendingValidateSupplier: isPending,
  };
};
